import { Link } from "react-router-dom";
import { ArrowLeft, ArrowRight, ShieldCheck, Lock, KeyRound, Server, Eye, FileCheck2, AlertTriangle, Building2, RadioTower } from "lucide-react";
import { Logo } from "../components/Logo";

const pillars = [
  { icon: Server, title: "On-premise by architecture", text: "Voice never has a path to the cloud — not a policy setting, a structural fact. The uplink protocol only accepts numeric score payloads; there is no endpoint capable of receiving audio." },
  { icon: Lock, title: "Encryption everywhere", text: "TLS on every console and sync connection. Passwords are hashed with bcrypt and never stored or transmitted in plain text." },
  { icon: Building2, title: "Strict tenant isolation", text: "Every workspace is scoped by company at the database layer. One organisation's employees, SOPs and scores are never visible to another." },
  { icon: KeyRound, title: "Key-authenticated sync", text: "Your local server authenticates to the cloud with a dedicated sync key that grants access only to the sync interfaces — never to console administration or other tenants' data." },
  { icon: Eye, title: "Full audit trail", text: "Every sync event — SOP pushes, device assignments, score uploads — is logged and visible in your console's activity feed, with real online/offline status for your local server." },
  { icon: ShieldCheck, title: "Hardened authentication", text: "Short-lived, httpOnly, secure session cookies and brute-force lockout on repeated failed sign-ins protect every workspace." },
];

const practices = [
  "Server-side validation on every write — the console never trusts client-supplied state for anything that affects another user's data.",
  "Least-privilege database roles: each backend process authenticates with credentials scoped to only the collections it needs.",
  "Company data is deleted on request; employee and SOP deletion are immediate and irreversible from the console.",
  "We do not use customer data — audio, transcripts, scores, or SOPs — to train any model.",
  "We do not sell customer data or share it with third parties beyond infrastructure providers strictly necessary to operate the Service.",
];

export default function Trust() {
  return (
    <div className="min-h-screen bg-[#f9f9f8] text-[#111]">
      <header className="fixed top-0 inset-x-0 z-50 bg-[#f9f9f8]/85 backdrop-blur-xl border-b border-neutral-200">
        <div className="max-w-6xl mx-auto px-6 h-16 flex items-center justify-between">
          <Link to="/" data-testid="trust-logo-link"><Logo /></Link>
          <Link to="/" data-testid="trust-back-link" className="inline-flex items-center gap-1.5 text-sm text-neutral-600 hover:text-[#111] transition-colors">
            <ArrowLeft className="w-4 h-4" /> Back to site
          </Link>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 pt-36 pb-28">
        <div className="fade-up max-w-2xl">
          <div className="inline-flex items-center gap-2 text-xs font-mono-data uppercase tracking-[0.2em] text-neutral-500 mb-6">
            <ShieldCheck className="w-4 h-4" /> Security &amp; Trust Center
          </div>
          <h1 className="font-heading font-light text-4xl sm:text-5xl tracking-tighter leading-[1.05]" data-testid="trust-title">
            Built for the buyers who read the architecture <span className="font-serif-accent italic text-neutral-500">before the pricing page.</span>
          </h1>
          <p className="mt-6 text-sm text-neutral-600 leading-relaxed">
            EXOPILOT's central design decision — that voice never leaves your premises — removes an entire category of risk before any other control matters. Everything below is how we protect what's left: your account data, your SOPs, and the numeric scores your local server reports.
          </p>
        </div>

        <div className="mt-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="trust-pillars-grid">
          {pillars.map((p) => (
            <div key={p.title} className="bg-white border border-neutral-200 rounded-lg p-7 hover:-translate-y-0.5 hover:shadow-lg hover:border-neutral-300 transition-[transform,box-shadow,border-color] duration-200">
              <span className="w-10 h-10 rounded-md bg-neutral-50 border border-neutral-200 flex items-center justify-center mb-5">
                <p.icon className="w-5 h-5 text-[#111]" />
              </span>
              <h3 className="text-sm font-medium">{p.title}</h3>
              <p className="text-xs text-neutral-500 leading-relaxed mt-2">{p.text}</p>
            </div>
          ))}
        </div>

        <div className="mt-20 grid grid-cols-1 md:grid-cols-12 gap-10">
          <div className="md:col-span-5">
            <p className="font-mono-data text-xs uppercase tracking-[0.2em] text-neutral-500 mb-4">Data handling practices</p>
            <h2 className="font-heading font-light text-2xl tracking-tight leading-snug">What we do — and what we deliberately don't.</h2>
            <p className="mt-4 text-sm text-neutral-500 leading-relaxed">The full legal detail lives in our <Link to="/privacy" className="text-[#111] underline underline-offset-4">Privacy Policy</Link>; this is the plain-language version enterprise security teams ask for first.</p>
          </div>
          <div className="md:col-span-7">
            <ul className="space-y-3" data-testid="trust-practices-list">
              {practices.map((p, i) => (
                <li key={i} className="flex gap-3 bg-white border border-neutral-200 rounded-lg px-5 py-4 text-sm text-neutral-600 leading-relaxed">
                  <ShieldCheck className="w-4 h-4 mt-0.5 shrink-0 text-neutral-800" /> {p}
                </li>
              ))}
            </ul>
          </div>
        </div>

        <div className="mt-20 bg-[#111] text-[#ededed] rounded-lg p-9 md:p-12" data-testid="trust-architecture">
          <p className="font-mono-data text-xs uppercase tracking-[0.2em] text-neutral-500 mb-4">Architecture summary</p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-neutral-800 border border-neutral-800 rounded-lg overflow-hidden mt-6">
            {[
              { icon: RadioTower, label: "Pendant + phone app", detail: "Records on the floor, relays over BLE to your premises. Never talks to the internet directly." },
              { icon: Server, label: "Your local server", detail: "Offline AI scoring against your SOPs. Voice and transcripts stay here, under your control, always." },
              { icon: ShieldCheck, label: "EXOPILOT cloud", detail: "Receives numeric scores and SOP mappings only, over a key-authenticated channel. Nothing else." },
            ].map((s) => (
              <div key={s.label} className="bg-[#161616] p-8">
                <s.icon className="w-5 h-5 text-neutral-400 mb-6" />
                <h3 className="font-heading text-base font-medium mb-2">{s.label}</h3>
                <p className="text-xs text-neutral-400 leading-relaxed">{s.detail}</p>
              </div>
            ))}
          </div>
        </div>

        <div className="mt-20 grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="bg-white border border-neutral-200 rounded-lg p-8" data-testid="trust-vulnerability-card">
            <span className="w-10 h-10 rounded-md bg-neutral-50 border border-neutral-200 flex items-center justify-center mb-5">
              <AlertTriangle className="w-5 h-5 text-[#111]" />
            </span>
            <h3 className="text-sm font-medium">Report a vulnerability</h3>
            <p className="text-xs text-neutral-500 leading-relaxed mt-2">Found a security issue? We want to hear about it before anyone else does. We'll acknowledge reports within one business day.</p>
            <a href="mailto:security@exopilot.io" data-testid="trust-security-contact-link" className="mt-5 inline-flex items-center gap-2 text-sm font-medium text-[#111] border-b border-neutral-400 pb-0.5 hover:border-[#111] transition-colors">
              security@exopilot.io <ArrowRight className="w-4 h-4" />
            </a>
          </div>
          <div className="bg-white border border-neutral-200 rounded-lg p-8" data-testid="trust-legal-card">
            <span className="w-10 h-10 rounded-md bg-neutral-50 border border-neutral-200 flex items-center justify-center mb-5">
              <FileCheck2 className="w-5 h-5 text-[#111]" />
            </span>
            <h3 className="text-sm font-medium">Policies &amp; agreements</h3>
            <p className="text-xs text-neutral-500 leading-relaxed mt-2">Everything your legal and procurement teams need in one place.</p>
            <div className="mt-5 flex flex-wrap gap-x-6 gap-y-2 text-sm">
              <Link to="/privacy" className="text-[#111] underline underline-offset-4">Privacy Policy</Link>
              <Link to="/terms" className="text-[#111] underline underline-offset-4">Terms of Service</Link>
              <Link to="/cookies" className="text-[#111] underline underline-offset-4">Cookies Policy</Link>
            </div>
          </div>
        </div>
      </main>

      <footer className="border-t border-neutral-200 px-6 py-10">
        <div className="max-w-6xl mx-auto flex items-center justify-between text-sm text-neutral-500">
          <Logo />
          <p className="text-xs">© 2026 EXOPILOT</p>
        </div>
      </footer>
    </div>
  );
}
